import React, { useId, useMemo } from 'react';
import './HeroBackdrop.css';

// Ambient "motion field" backdrop — a stylized echo of the editing
// page's MotionChart. Dots are laid out on a jittered grid so they
// fill the area evenly; the motion envelope only decides which dots
// glow teal (i.e., would be "above threshold" in the chart). A sparse,
// staggered subset of dots gets gentle bounces or glow flairs so the
// field feels alive without anything moving in unison.

const PATTERN_WIDTH = 1600;
const VIEW_HEIGHT = 600;
const DOT_COLUMNS = 30;
const DOT_ROWS = 12;
const JITTER = 1.45;

const BURSTS = [
  { x: 0.06, y: 0.32, r: 0.14, w: 0.7 },
  { x: 0.18, y: 0.62, r: 0.18, w: 0.85 },
  { x: 0.31, y: 0.28, r: 0.16, w: 0.75 },
  { x: 0.44, y: 0.7, r: 0.2, w: 0.95 },
  { x: 0.58, y: 0.36, r: 0.17, w: 0.8 },
  { x: 0.7, y: 0.66, r: 0.16, w: 0.75 },
  { x: 0.82, y: 0.3, r: 0.18, w: 0.85 },
  { x: 0.94, y: 0.6, r: 0.14, w: 0.7 },
];

function envelopeAt(tx, ty) {
  let total = 0;
  for (const b of BURSTS) {
    const dxRaw = Math.abs(tx - b.x);
    const dx = Math.min(dxRaw, 1 - dxRaw);
    const dy = ty - b.y;
    const d = Math.sqrt(dx * dx + dy * dy);
    total += b.w * Math.exp(-(d * d) / (2 * b.r * b.r));
  }
  return total;
}

function mulberry32(seed) {
  let a = seed >>> 0;
  return function () {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function buildPattern() {
  const rng = mulberry32(0xC0FFEE);

  // Jittered grid layout: each cell holds one dot at a randomized
  // position. Result is an even, organic-looking distribution with no
  // clustering and no visible grid.
  const dots = [];

  for (let row = 0; row < DOT_ROWS; row++) {
    // Hex-style half-cell offset on odd rows breaks orthogonal alignment
    // before any jitter is applied, so the eye never finds rows/columns.
    const rowOffset = row % 2 === 0 ? 0 : 0.5;
    for (let col = 0; col < DOT_COLUMNS; col++) {
      const jx = (rng() - 0.5) * JITTER;
      const jy = (rng() - 0.5) * JITTER;
      // Wrap horizontally so the half-offset on odd rows still tiles
      // cleanly across the pattern width.
      const tx = (((col + 0.5 + rowOffset + jx) % DOT_COLUMNS) + DOT_COLUMNS) % DOT_COLUMNS / DOT_COLUMNS;
      const ty = (row + 0.5 + jy) / DOT_ROWS;
      const x = tx * PATTERN_WIDTH;
      const y = ty * VIEW_HEIGHT;

      // Active (teal) where the soft motion envelope is loud enough.
      const env = envelopeAt(tx, ty);
      const active = env > 0.45;

      // Sparse animation roll — only a minority of dots ever move at
      // once, with active dots more likely to get the glow flair.
      const roll = rng();
      let anim = 'none';
      if (active) {
        if (roll < 0.2) anim = 'flair';
        else if (roll < 0.3) anim = 'bounce';
      } else if (roll < 0.1) {
        anim = 'bounce';
      }

      // Per-dot timing — wide delay window plus varied durations so
      // bounces never sync up. Keyframes also bake in a long rest so
      // animated dots only move ~30% of their cycle.
      const delay = (rng() * 28).toFixed(2);
      const duration = (6 + rng() * 6).toFixed(2);
      const amplitude = (4 + rng() * 8).toFixed(1);

      dots.push({ x, y, active, anim, delay, duration, amplitude });
    }
  }

  return { dots };
}

function HeroBackdrop() {
  const { dots } = useMemo(buildPattern, []);
  const idPrefix = useId();
  const fadeId = `${idPrefix}fade`;
  const bottomFadeId = `${idPrefix}bfade`;
  const textFadeId = `${idPrefix}tfade`;
  const glowId = `${idPrefix}glow`;

  return (
    <div className="hero-backdrop" aria-hidden="true">
      <svg
        className="hero-backdrop-svg"
        viewBox={`0 0 ${PATTERN_WIDTH} ${VIEW_HEIGHT}`}
        preserveAspectRatio="xMidYMin slice"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          {/* Side edge fade — softens the left/right boundaries. */}
          <linearGradient id={fadeId} x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor="#121212" stopOpacity="0.75" />
            <stop offset="10%" stopColor="#121212" stopOpacity="0" />
            <stop offset="90%" stopColor="#121212" stopOpacity="0" />
            <stop offset="100%" stopColor="#121212" stopOpacity="0.75" />
          </linearGradient>

          {/* Bottom fade only — top stays bright so it reads as flush
              with the header instead of looking like a gap. */}
          <linearGradient id={bottomFadeId} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#121212" stopOpacity="0" />
            <stop offset="70%" stopColor="#121212" stopOpacity="0" />
            <stop offset="100%" stopColor="#121212" stopOpacity="0.7" />
          </linearGradient>

          {/* Full-width vertical darkening behind the hero text column —
              title, subtitle, CTAs, trust line, and legal notice. The
              gradientTransform pivots around the center, stretches the
              ellipse vertically (1.85) so the darkening covers the full
              hero height, and slightly over-extends horizontally (1.05)
              so even wide viewports stay dark enough that dots don't
              read through behind text. The horizontal falloff is gentle
              by design — a tighter ellipse leaves visible dots behind
              the headline at >1600px widths. Residual opacity at the
              SVG edges is ~0.36, which the side `fadeId` overlay (0.75
              at the edges) then absorbs into the page background. */}
          <radialGradient
            id={textFadeId}
            cx="50%"
            cy="50%"
            r="55%"
            fx="50%"
            fy="50%"
            gradientTransform="translate(0.5 0.5) scale(1.05 1.85) translate(-0.5 -0.5)"
          >
            <stop offset="0%" stopColor="#121212" stopOpacity="0.95" />
            <stop offset="40%" stopColor="#121212" stopOpacity="0.92" />
            <stop offset="65%" stopColor="#121212" stopOpacity="0.78" />
            <stop offset="85%" stopColor="#121212" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#121212" stopOpacity="0" />
          </radialGradient>

          <radialGradient id={glowId} cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#00B3A6" stopOpacity="0.5" />
            <stop offset="60%" stopColor="#00B3A6" stopOpacity="0.08" />
            <stop offset="100%" stopColor="#00B3A6" stopOpacity="0" />
          </radialGradient>
        </defs>

        <g className="hero-backdrop-dots">
          {dots.map((d, i) => {
            const className = [
              'hero-backdrop-dot',
              d.active ? 'active' : '',
              d.anim !== 'none' ? d.anim : '',
            ]
              .filter(Boolean)
              .join(' ');

            const style =
              d.anim === 'none'
                ? undefined
                : {
                    animationDelay: `${d.delay}s`,
                    animationDuration: `${d.duration}s`,
                    '--bounce-amp': `${-d.amplitude}px`,
                  };

            return (
              <circle
                key={i}
                cx={d.x}
                cy={d.y}
                r={d.active ? 2.4 : 1.6}
                className={className}
                style={style}
              />
            );
          })}
        </g>

        {/* Soft ambient glow blobs anchored to a couple of envelope
            peaks — barely visible, just enough to give depth. */}
        <circle
          cx={PATTERN_WIDTH * 0.44}
          cy={VIEW_HEIGHT * 0.7}
          r={220}
          fill={`url(#${glowId})`}
          className="hero-backdrop-glow glow-a"
        />
        <circle
          cx={PATTERN_WIDTH * 0.82}
          cy={VIEW_HEIGHT * 0.3}
          r={180}
          fill={`url(#${glowId})`}
          className="hero-backdrop-glow glow-b"
        />

        {/* Overlays — order matters. Text-darkening sits on top of the
            dots so the field fades toward black behind the headline. */}
        <rect
          x="0"
          y="0"
          width={PATTERN_WIDTH}
          height={VIEW_HEIGHT}
          fill={`url(#${bottomFadeId})`}
        />
        <rect
          x="0"
          y="0"
          width={PATTERN_WIDTH}
          height={VIEW_HEIGHT}
          fill={`url(#${fadeId})`}
        />
        <rect
          x="0"
          y="0"
          width={PATTERN_WIDTH}
          height={VIEW_HEIGHT}
          fill={`url(#${textFadeId})`}
          className="hero-backdrop-text-fade"
        />
      </svg>
    </div>
  );
}

export default HeroBackdrop;
